import React, { useEffect, useState } from 'react';
import { dbService } from '../firebaseConfig';
import { EventSettings } from '../types';
import { INCLUDES } from '../eventData';

interface LandingPageProps {
  onStartRegistration: () => void;
}

// Separa o emoji inicial de uma linha de aviso ("🚌 Saída em grupo..." → ["🚌", "Saída em grupo..."])
const splitIcon = (line: string): [string, string] => {
  const m = line.match(/^(\S{1,4})\s+(.*)$/u);
  if (m && /\p{Extended_Pictographic}/u.test(m[1])) return [m[1], m[2]];
  return ['•', line];
};

const SectionTitle: React.FC<{ kicker: string; title: string; dark?: boolean; align?: 'center' | 'left' }> = ({ kicker, title, dark, align = 'center' }) => (
  <div className={`mb-14 ${align === 'center' ? 'text-center' : 'text-left'}`}>
    <p className={`text-[11px] font-black uppercase tracking-[0.45em] mb-4 ${dark ? 'text-brand-amber' : 'text-brand-ember'}`}>{kicker}</p>
    <h2 className={`font-display text-5xl md:text-6xl font-semibold uppercase tracking-tight leading-none ${dark ? 'text-white' : 'text-brand-dark'}`}>{title}</h2>
    <div className={`w-20 h-1.5 mt-6 rounded-full ${align === 'center' ? 'mx-auto' : ''} ${dark ? 'bg-brand-amber' : 'bg-brand-green'}`}></div>
  </div>
);

const LandingPage: React.FC<LandingPageProps> = ({ onStartRegistration }) => {
  const [settings, setSettings] = useState<EventSettings | null>(null);
  const [error, setError] = useState(false);
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    let mounted = true;
    dbService.getSettings()
      .then(s => {
        if (mounted) setSettings(s);
      })
      .catch(e => {
        console.error("Erro ao carregar configurações:", e);
        if (mounted) setError(true);
      });
    return () => { mounted = false; };
  }, []);

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-sand-soft px-4 text-center">
      <h2 className="text-brand-dark font-display text-4xl font-semibold uppercase mb-4">Ops! Algo deu errado</h2>
      <p className="text-gray-500 mb-8">Não conseguimos conectar com o servidor. Verifique sua internet.</p>
      <button onClick={() => window.location.reload()} className="bg-brand-amber text-brand-night px-8 py-3 rounded-2xl font-bold">Tentar Novamente</button>
    </div>
  );

  if (!settings) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-night">
      <div className="w-12 h-12 border-4 border-white/10 border-t-brand-amber rounded-full animate-spin"></div>
    </div>
  );

  const [mainSpeaker, ...otherSpeakers] = settings.speakers;
  const leftSpeaker = otherSpeakers[0];
  const rightSpeaker = otherSpeakers[1];
  // Nos cards, o preletor principal (primeiro da lista) fica no meio
  const cardSpeakers = settings.speakers.length >= 3
    ? [settings.speakers[1], settings.speakers[0], ...settings.speakers.slice(2)]
    : settings.speakers;
  const day = settings.schedule[Math.min(activeDay, settings.schedule.length - 1)];
  const hasPrice = settings.eventPrice > 0;

  // Mapa da chácara: o endereço puro não geocodifica direito, então o embed tem prioridade
  const retreatEmbed = settings.retreatMapEmbed
    || (settings.retreatAddress ? `https://maps.google.com/maps?q=${encodeURIComponent(settings.retreatAddress)}&t=&z=15&ie=UTF8&iwloc=&output=embed` : '');
  const retreatMapsHref = settings.retreatMapsLink
    || (settings.retreatAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.retreatAddress)}` : '');

  const CTA = ({ light }: { light?: boolean }) => settings.isRegistrationClosed ? (
    <div className={`px-12 py-6 rounded-2xl text-lg font-black uppercase tracking-widest border-2 border-dashed w-full sm:w-auto text-center ${light ? 'border-white/20 text-white/50 bg-white/5' : 'border-gray-200 text-gray-400 bg-gray-100'}`}>
      Inscrições Encerradas
    </div>
  ) : (
    <button
      onClick={onStartRegistration}
      className={`px-12 py-6 rounded-2xl text-lg font-black uppercase tracking-widest transition-all shadow-2xl transform hover:-translate-y-1 active:scale-95 w-full sm:w-auto ${light ? 'bg-white text-brand-night hover:bg-brand-amber' : 'bg-brand-amber text-brand-night hover:bg-white shadow-brand-amber/30'}`}
    >
      Garantir minha vaga{hasPrice ? ` • R$ ${settings.eventPrice}` : ''}
    </button>
  );

  return (
    <div className="animate-in fade-in duration-1000">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-night-grain text-white">
        <div className="absolute inset-0 texture-lines pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 pt-16 pb-0 md:pt-24 flex flex-col lg:flex-row items-center gap-12 lg:gap-8">

          {/* Texto */}
          <div className="flex-1 text-center lg:text-left relative z-10 pb-12 lg:pb-32">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/5 border border-white/10 text-brand-amber rounded-full text-[10px] font-black mb-10 tracking-[0.25em] uppercase">
              <span className="w-2 h-2 bg-brand-amber rounded-full animate-pulse"></span>
              Evento Presencial • {settings.eventDate} de {settings.eventYear}
            </div>

            <h1 className="font-display font-bold uppercase leading-[0.9] tracking-tight mb-8">
              <span className="block text-6xl md:text-8xl lg:text-[7.5rem]">{settings.eventName}</span>
              <span className="block text-brand-amber text-4xl md:text-6xl lg:text-7xl mt-3 font-medium tracking-[0.08em]">{settings.eventEdition}</span>
            </h1>

            <p className="text-lg md:text-xl text-white/60 mb-3 italic max-w-xl mx-auto lg:mx-0 leading-relaxed">
              "{settings.eventVerse}"
            </p>
            <p className="text-[11px] font-black uppercase tracking-[0.35em] text-brand-amber/80 mb-12">{settings.eventVerseRef}</p>

            <div className="flex flex-col sm:flex-row items-center gap-4 mb-14 justify-center lg:justify-start">
              <CTA />
              <a href="#programacao" className="px-8 py-6 rounded-2xl border border-white/15 text-white/80 text-sm font-black uppercase tracking-widest hover:bg-white/5 transition-colors w-full sm:w-auto text-center">
                Ver programação ↓
              </a>
            </div>

            {settings.paymentInfo && (
              <p className="-mt-8 mb-12 text-white/60 text-sm leading-relaxed flex items-start justify-center lg:justify-start gap-2 max-w-xl mx-auto lg:mx-0">
                <span>💳</span>
                <span>{settings.paymentInfo}</span>
              </p>
            )}

            <div className="flex flex-wrap items-stretch justify-center lg:justify-start gap-4">
              <div className="bg-white/5 border border-white/10 backdrop-blur px-6 py-5 rounded-2xl min-w-[130px] text-left">
                <p className="font-display text-4xl font-semibold leading-none">3 <span className="text-brand-amber text-2xl">dias</span></p>
                <p className="text-[10px] uppercase font-black text-white/50 mt-2 tracking-widest">2 noites de imersão</p>
              </div>
              <div className="bg-white/5 border border-white/10 backdrop-blur px-6 py-5 rounded-2xl min-w-[130px] text-left">
                <p className="font-display text-4xl font-semibold leading-none">18<span className="text-brand-amber text-2xl">h45</span></p>
                <p className="text-[10px] uppercase font-black text-white/50 mt-2 tracking-widest">Chegada sexta • Chácara</p>
              </div>
              <div className="bg-white/5 border border-white/10 backdrop-blur px-6 py-5 rounded-2xl min-w-[130px] text-left">
                <p className="font-display text-2xl font-semibold leading-none mt-1">🏕️ {settings.retreatName}</p>
                <p className="text-[10px] uppercase font-black text-white/50 mt-2 tracking-widest">Hospedagem e programação</p>
              </div>
            </div>
          </div>

          {/* Preletores (composição) */}
          <div className="flex-1 w-full max-w-2xl relative self-end">
            <div className="absolute left-1/2 -translate-x-1/2 bottom-10 w-[90%] h-[70%] rounded-full bg-brand-amber/25 blur-3xl ember-glow pointer-events-none"></div>
            {/* Tamanhos relativos à largura do contêiner para nunca cortar ninguém */}
            <div className="relative flex items-end justify-center pt-6">
              {leftSpeaker && (
                <img src={leftSpeaker.image} alt={leftSpeaker.name} className="w-[42%] h-auto object-contain -mr-[11%] opacity-90 drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]" />
              )}
              {mainSpeaker && (
                <img src={mainSpeaker.image} alt={mainSpeaker.name} className="w-[58%] h-auto object-contain relative z-10 drop-shadow-[0_30px_60px_rgba(0,0,0,0.7)]" />
              )}
              {rightSpeaker && (
                <img src={rightSpeaker.image} alt={rightSpeaker.name} className="w-[42%] h-auto object-contain -ml-[11%] opacity-90 drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]" />
              )}
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 bg-brand-night/60 backdrop-blur-md border border-white/10 rounded-2xl px-7 py-3 text-center whitespace-nowrap">
              <p className="text-[9px] uppercase tracking-[0.4em] text-brand-amber font-black mb-1.5">Preletores</p>
              {settings.speakers.map(s => (
                <p key={s.id} className="font-display text-sm md:text-base uppercase tracking-wide text-white/90 leading-snug">{s.name}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= PRELETORES ================= */}
      <section id="preletores" className="py-28 px-4 bg-sand-soft scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <SectionTitle kicker="Quem vai ministrar" title="Preletores" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cardSpeakers.map((sp, idx) => (
              <div key={sp.id} className="group relative rounded-[36px] overflow-hidden bg-brand-dark text-white shadow-2xl shadow-brand-dark/20 hover:-translate-y-2 transition-all duration-500">
                <div className={`absolute inset-0 ${idx % 2 === 0 ? 'bg-gradient-to-br from-brand-moss via-brand-dark to-brand-night' : 'bg-gradient-to-br from-brand-ember/70 via-brand-dark to-brand-night'}`}></div>
                <div className="absolute inset-0 texture-lines"></div>
                <div className="relative h-[380px] flex items-end justify-center pt-8 overflow-hidden">
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-brand-amber/20 blur-3xl"></div>
                  <img src={sp.image} alt={sp.name} className="relative h-[92%] w-auto object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)] group-hover:scale-[1.04] transition-transform duration-700" />
                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-brand-night to-transparent"></div>
                </div>
                <div className="relative px-8 pb-8 -mt-6">
                  <p className="text-[9px] uppercase tracking-[0.4em] text-brand-amber font-black mb-2">{sp.sessions || 'Preletor'}</p>
                  <h3 className="font-display text-3xl font-semibold uppercase leading-none mb-2">{sp.name}</h3>
                  <p className="text-white/60 text-sm">{sp.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INCLUSO ================= */}
      <section className="py-28 bg-brand-paper px-4 relative">
        <div className="max-w-6xl mx-auto">
          <SectionTitle kicker="Três dias de imersão" title="Sua inscrição inclui" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INCLUDES.map((item, idx) => (
              <div key={idx} className="bg-white p-9 rounded-[32px] border border-brand-sand shadow-sm hover:shadow-2xl hover:shadow-brand-dark/10 hover:-translate-y-2 transition-all duration-500 group">
                <div className="w-14 h-14 bg-brand-sand rounded-2xl flex items-center justify-center text-3xl mb-7 group-hover:bg-brand-amber/20 group-hover:rotate-6 transition-all">{item.icon}</div>
                <h4 className="font-display text-2xl font-semibold text-brand-dark mb-3 leading-tight uppercase tracking-wide">{item.title}</h4>
                <p className="text-brand-stone leading-relaxed text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-brand-dark text-white p-9 rounded-[32px] flex items-center gap-7 group">
              <span className="text-5xl transform group-hover:scale-110 transition-transform">📖</span>
              <div>
                <h5 className="font-black text-brand-amber uppercase text-[10px] tracking-[0.3em] mb-2">Preparação</h5>
                <p className="font-display text-2xl font-semibold uppercase leading-tight">Traga sua Bíblia</p>
                <p className="text-white/50 text-sm mt-2">Para mergulharmos juntos na Palavra de Deus.</p>
              </div>
            </div>
            <div className="bg-white border border-brand-sand p-9 rounded-[32px] flex items-center gap-7 group">
              <span className="text-5xl transform group-hover:scale-110 transition-transform">🧭</span>
              <div>
                <h5 className="font-black text-brand-stone uppercase text-[10px] tracking-[0.3em] mb-2">Observação</h5>
                <p className="font-display text-2xl font-semibold uppercase leading-tight text-brand-dark">Evento exclusivo para homens</p>
                <p className="text-brand-stone text-sm mt-2">Chegada direto na Chácara na sexta; culto de encerramento na Igreja no domingo.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= PROGRAMAÇÃO ================= */}
      <section id="programacao" className="py-28 px-4 bg-night-grain text-white relative scroll-mt-20">
        <div className="absolute inset-0 texture-lines pointer-events-none"></div>
        <div className="max-w-5xl mx-auto relative">
          <SectionTitle kicker={`${settings.eventDate} de ${settings.eventYear}`} title="Programação" dark />

          {/* Abas dos dias */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {settings.schedule.map((d, i) => (
              <button
                key={d.id}
                onClick={() => setActiveDay(i)}
                className={`px-6 py-4 rounded-2xl border transition-all text-left min-w-[150px] ${i === activeDay ? 'bg-brand-amber border-brand-amber text-brand-night shadow-xl shadow-brand-amber/20' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
              >
                <p className="font-display text-xl font-semibold uppercase leading-none">{d.label}</p>
                <p className={`text-[10px] font-black uppercase tracking-widest mt-1.5 ${i === activeDay ? 'text-brand-night/70' : 'text-white/40'}`}>{d.date}</p>
              </button>
            ))}
          </div>

          {day && (
            <div className="bg-white/5 border border-white/10 rounded-[36px] p-6 md:p-10 backdrop-blur animate-in fade-in duration-500" key={day.id}>
              <div className="flex items-center gap-3 mb-8 text-brand-amber">
                <span className="text-xl">📍</span>
                <p className="text-[11px] font-black uppercase tracking-[0.3em]">{day.place}</p>
              </div>
              <ol className="relative border-l border-white/10 ml-4 md:ml-6 space-y-2">
                {day.items.map((it, i) => (
                  <li key={i} className="relative pl-8 md:pl-10 py-3 group">
                    <span className="absolute -left-[7px] top-[22px] w-3.5 h-3.5 rounded-full bg-brand-night border-2 border-brand-amber group-hover:bg-brand-amber transition-colors"></span>
                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6">
                      <p className="font-display text-2xl md:text-3xl font-semibold text-brand-amber leading-none sm:w-24 shrink-0">{it.time}</p>
                      <div>
                        <p className="font-display text-xl md:text-2xl uppercase tracking-wide leading-tight">
                          {it.icon && <span className="mr-2">{it.icon}</span>}{it.activity}
                        </p>
                        {it.who && <p className="text-white/50 text-sm mt-1">{it.who}</p>}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <p className="text-center text-white/30 text-xs mt-8 italic">A programação pode sofrer pequenos ajustes.</p>
        </div>
      </section>

      {/* ================= O QUE LEVAR ================= */}
      <section id="levar" className="py-28 px-4 bg-sand-soft scroll-mt-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <SectionTitle kicker="Checklist do participante" title="O que levar" align="left" />
            <p className="text-brand-stone leading-relaxed mb-8 -mt-6">
              Vamos ficar duas noites na {settings.retreatName}. Prepare sua mochila com os itens ao lado — a lista também fica no seu comprovante de inscrição.
            </p>
            <CTA />
          </div>
          <div className="lg:col-span-3 bg-white rounded-[36px] border border-brand-sand shadow-xl p-8 md:p-10">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {settings.checklist.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center text-xs font-black shrink-0">✓</span>
                  <span className="text-brand-dark text-sm font-medium leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ================= LOCAL ================= */}
      <section id="local" className="py-28 px-4 bg-brand-paper scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <SectionTitle kicker="Onde vai acontecer" title="Localização" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Chácara — chegada e hospedagem */}
            <div className="bg-brand-dark text-white rounded-[40px] shadow-2xl overflow-hidden relative flex flex-col">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-moss via-brand-dark to-brand-night"></div>
              <div className="absolute inset-0 texture-lines"></div>
              <div className="relative p-8 md:p-10">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-amber mb-3">Chegada sexta às 18h45 • Hospedagem até domingo</p>
                <h3 className="font-display text-3xl font-semibold uppercase leading-none mb-2">🏕️ {settings.retreatName}</h3>
                <p className="text-white/60 text-sm mb-6">{settings.retreatAddress || 'Endereço e orientações de deslocamento serão enviados aos inscritos.'}</p>

                {(settings.retreatNotes && settings.retreatNotes.length > 0) && (
                  <ul className="space-y-4 text-sm text-white/80 mb-6">
                    {settings.retreatNotes.map((note, i) => {
                      const [icon, text] = splitIcon(note);
                      return <li key={i} className="flex gap-3"><span>{icon}</span><span>{text}</span></li>;
                    })}
                  </ul>
                )}

                {retreatMapsHref && (
                  <a
                    href={retreatMapsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex self-start items-center gap-2 bg-brand-amber text-brand-night px-6 py-3.5 rounded-2xl font-bold text-sm shadow-lg hover:bg-white transition-all active:scale-95"
                  >
                    <span>📍</span> Como chegar
                  </a>
                )}
              </div>
              {retreatEmbed && (
                <div className="relative aspect-video w-full bg-brand-night border-t border-white/10 mt-auto">
                  <iframe
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    src={retreatEmbed}
                    className="absolute inset-0"
                  ></iframe>
                </div>
              )}
            </div>

            {/* Igreja — encerramento */}
            <div className="bg-white rounded-[40px] border border-brand-sand shadow-2xl overflow-hidden relative flex flex-col">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-brand-amber via-brand-ember to-brand-green"></div>
              <div className="p-8 md:p-10">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-ember mb-3">Domingo • Culto de Encerramento às 10h</p>
                <h3 className="font-display text-3xl font-semibold uppercase text-brand-dark leading-none mb-2">{settings.locationName}</h3>
                <p className="text-brand-stone text-sm mb-6">{settings.locationAddress}</p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.locationAddress)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-green text-white px-6 py-3.5 rounded-2xl font-bold text-sm shadow-lg hover:opacity-90 transition-all active:scale-95"
                >
                  <span>📍</span> Abrir no Maps
                </a>
              </div>
              <div className="aspect-video w-full bg-gray-100 relative border-t border-brand-sand mt-auto">
                <iframe
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(settings.locationAddress)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  className="absolute inset-0 grayscale contrast-110 opacity-80"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CTA FINAL ================= */}
      <section className="py-28 px-4 bg-sand-soft">
        <div className="max-w-4xl mx-auto bg-brand-night rounded-[48px] p-14 md:p-24 text-center text-white relative overflow-hidden shadow-[0_50px_100px_-20px_rgba(11,21,16,0.6)]">
          <div className="absolute inset-0 bg-night-grain"></div>
          <div className="absolute inset-0 texture-lines"></div>
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-brand-amber/15 rounded-full blur-3xl ember-glow"></div>

          <div className="relative">
            <h2 className="font-display text-5xl md:text-7xl font-semibold uppercase mb-8 leading-[0.95]">Três dias para<br />se fortalecer.</h2>
            <p className="text-white/60 text-lg md:text-xl mb-12 max-w-xl mx-auto leading-relaxed italic">
              "Sede fortes e corajosos; não temais... porque o Senhor, vosso Deus, é quem vai convosco."
              <span className="block not-italic text-[10px] font-black uppercase tracking-[0.3em] text-brand-amber mt-3">Deuteronômio 31:6</span>
            </p>
            <div className="flex justify-center">
              <CTA light />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
